/**
 * Componente de Árvore de Permissões Hierárquica
 * Exibe permissões em estrutura de árvore com checkboxes
 */

import React, { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronRight, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export interface Permission {
  id: number;
  name: string;
  display_name: string;
  description?: string;
  level: number;
  is_category: boolean;
  parent_id?: number | null;
  permission_type?: string;
  icon?: string;
  display_order: number;
  children?: Permission[];
}

interface PermissionTreeProps {
  permissions: Permission[];
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
  readOnly?: boolean;
}

interface TreeNodeProps {
  node: Permission;
  isSelected: boolean;
  isIndeterminate: boolean;
  onToggle: (id: number, checked: boolean) => void;
  depth: number;
  readOnly?: boolean;
}

const getIcon = (permissionType?: string, level?: number) => {
  if (permissionType === 'create') return '➕';
  if (permissionType === 'read') return '👁️';
  if (permissionType === 'update') return '✏️';
  if (permissionType === 'delete') return '🗑️';
  if (permissionType === 'execute') return '⚡';
  if (level === 1) return '📁';
  if (level === 2) return '📂';
  return '✓';
};

const TreeNode: React.FC<TreeNodeProps> = ({
  node,
  isSelected,
  isIndeterminate,
  onToggle,
  depth,
  readOnly = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(depth < 2); // Expandir categorias e recursos por padrão
  const hasChildren = node.children && node.children.length > 0;

  const handleCheckboxChange = (checked: boolean) => {
    if (!readOnly) {
      onToggle(node.id, checked);
    }
  };

  return (
    <div className="select-none">
      <div
        className={cn(
          "flex items-center py-2 px-2 hover:bg-muted/50 rounded-md transition-colors",
          isSelected && "bg-muted/30"
        )}
        style={{ paddingLeft: `${depth * 24 + 8}px` }}
      >
        {/* Botão de expandir/colapsar */}
        {hasChildren ? (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mr-2 p-0.5 hover:bg-muted rounded"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        ) : (
          <div className="w-5 mr-2" />
        )}

        {/* Checkbox */}
        <Checkbox
          id={`perm-${node.id}`}
          checked={isSelected}
          onCheckedChange={handleCheckboxChange}
          disabled={readOnly}
          className={cn(
            isIndeterminate && "data-[state=checked]:bg-primary/50"
          )}
        />

        {/* Ícone e nome */}
        <label
          htmlFor={`perm-${node.id}`}
          className={cn(
            "ml-3 flex items-center space-x-2 cursor-pointer flex-1",
            readOnly && "cursor-default"
          )}
        >
          <span className="text-lg">
            {getIcon(node.permission_type, node.level)}
          </span>
          <div className="flex-1">
            <span
              className={cn(
                "text-sm font-medium",
                node.level === 1 && "text-base font-semibold",
                node.level === 2 && "text-sm font-medium",
                node.level === 3 && "text-sm"
              )}
            >
              {node.display_name}
            </span>
            {node.description && (
              <p className="text-xs text-muted-foreground">
                {node.description}
              </p>
            )}
          </div>
          <span className="text-xs text-muted-foreground font-mono">
            {node.name}
          </span>
        </label>
      </div>

      {/* Filhos */}
      {hasChildren && isExpanded && (
        <div>
          {node.children!.map((child) => (
            <ConnectedTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Componente conectado que recebe o contexto de seleção
const ConnectedTreeNode: React.FC<
  Omit<TreeNodeProps, "isSelected" | "isIndeterminate" | "onToggle">
> = ({ node, depth, readOnly }) => {
  const { selectedIds, onSelectionChange } = React.useContext(TreeContext);

  const isSelected = selectedIds.includes(node.id);

  // Verificar se algum filho está selecionado (estado indeterminado)
  const isIndeterminate = !isSelected && hasSelectedDescendant(node, selectedIds);

  const handleToggle = (id: number, checked: boolean) => {
    let newSelection: number[];

    if (checked) {
      // Adicionar este nó e todos os descendentes
      const descendants = getAllDescendants(node);
      newSelection = [...new Set([...selectedIds, id, ...descendants])];
    } else {
      // Remover este nó e todos os descendentes
      const descendants = getAllDescendants(node);
      newSelection = selectedIds.filter(
        (selId) => selId !== id && !descendants.includes(selId)
      );
    }

    onSelectionChange(newSelection);
  };

  return (
    <TreeNode
      node={node}
      isSelected={isSelected}
      isIndeterminate={isIndeterminate}
      onToggle={handleToggle}
      depth={depth}
      readOnly={readOnly}
    />
  );
};

// Contexto para compartilhar estado de seleção
const TreeContext = React.createContext<{
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
}>({
  selectedIds: [],
  onSelectionChange: () => {},
});

export const PermissionTree: React.FC<PermissionTreeProps> = ({
  permissions,
  selectedIds,
  onSelectionChange,
  readOnly = false,
}) => {
  // Construir árvore hierárquica
  const tree = buildTree(permissions);

  return (
    <TreeContext.Provider value={{ selectedIds, onSelectionChange }}>
      <div className="space-y-1">
        {tree.map((rootNode) => (
          <ConnectedTreeNode
            key={rootNode.id}
            node={rootNode}
            depth={0}
            readOnly={readOnly}
          />
        ))}
      </div>
    </TreeContext.Provider>
  );
};

// Função auxiliar para construir árvore
function buildTree(permissions: Permission[]): Permission[] {
  const tree: Permission[] = [];
  const map: { [key: number]: Permission } = {};

  // Criar mapa de todas as permissões
  permissions.forEach((perm) => {
    map[perm.id] = { ...perm, children: [] };
  });

  // Construir hierarquia
  permissions.forEach((perm) => {
    if (perm.parent_id === null || perm.parent_id === undefined) {
      tree.push(map[perm.id]);
    } else if (map[perm.parent_id]) {
      map[perm.parent_id].children!.push(map[perm.id]);
    }
  });

  // Ordenar por display_order
  const sortTree = (nodes: Permission[]) => {
    nodes.sort((a, b) => a.display_order - b.display_order);
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        sortTree(node.children);
      }
    });
  };

  sortTree(tree);

  return tree;
}

// Função auxiliar para obter todos os descendentes
function getAllDescendants(node: Permission): number[] {
  const descendants: number[] = [];

  const traverse = (n: Permission) => {
    if (n.children) {
      n.children.forEach((child) => {
        descendants.push(child.id);
        traverse(child);
      });
    }
  };

  traverse(node);
  return descendants;
}

// Função auxiliar para verificar se há descendente selecionado
function hasSelectedDescendant(node: Permission, selectedIds: number[]): boolean {
  if (!node.children) return false;

  for (const child of node.children) {
    if (selectedIds.includes(child.id)) return true;
    if (hasSelectedDescendant(child, selectedIds)) return true;
  }

  return false;
}

